import React, { useCallback, useEffect, useRef, useState } from 'react';
import ids from 'short-id';
import { first, identity, noop } from 'underscore';
import {
  getStateContent,
  useTemplateDispatch,
} from 'components/VideoTemplateEditor/state';
import { PodcastWorkflowTemplate } from 'redux/middleware/api/podcast-service';
import { EditorState } from '..';
import {
  MediaIntegrationId,
  OnIsAutoFrameChange,
  OnStateChange,
  VideoTemplateState,
} from '../types';
import {
  DynamicVideoIntegration,
  IntegrationProps,
  PlaceholderVideo,
} from '../types/integrations';
import useCreateVideoAsset from '../useCreateVideoAsset';
import VideoPodcastSettings from '../VideoPodcastSettings';

const INTEGRATION_ID = MediaIntegrationId.DYNAMIC as const;

interface UseDynamicVideoIntegrationConfig
  extends IntegrationProps,
    Pick<DynamicVideoIntegration, 'childViewProps'> {
  isActive: boolean;
  /**
   * `placeholderVideo` is a video asset created from user's uploaded video used
   * to populate the template when:
   * 1. The template is empty and needs an initial video clip.
   * 2. The template changes and existing video clips need to be replaced.
   * It contains the video source, file name, trim boundaries and framing method.
   * */
  placeholderVideo?: PlaceholderVideo;
  state: VideoTemplateState;
  template: PodcastWorkflowTemplate;
  onIsAutoFrameChange?: OnIsAutoFrameChange;
  onChange: OnStateChange;
}

export default function useDynamicVideoIntegration({
  isActive,
  state,
  template,
  placeholderVideo,
  priority,
  childViewProps,
  onIsAutoFrameChange,
  onChange,
}: UseDynamicVideoIntegrationConfig): DynamicVideoIntegration {
  const [isAddingPlaceholderVideo, setIsAddingPlaceholderVideo] = useState(
    false,
  );

  const prevTemplateRef = useRef(template);
  const isVideoReplacedRef = useRef(false);

  const { src, fileName, trimStartMillis, trimEndMillis, framingMethod } =
    placeholderVideo ?? {};
  const isAutoFrame = framingMethod === 'autoframe';
  const templateClipsCount = template?.previewConfiguration?.videoClips?.length;
  const stateClipsCount = EditorState.getContent(state)?.videoClips?.order
    ?.length;

  const { createVideoAsset } = useCreateVideoAsset();
  const templateDispatch = useTemplateDispatch({
    onChange,
  });

  const handleAddPlaceholderVideo = useCallback(async (): Promise<void> => {
    setIsAddingPlaceholderVideo(true);

    const { videoAsset, startMillis, endMillis } = await createVideoAsset(
      src,
      trimStartMillis,
      trimEndMillis,
    );

    templateDispatch({
      type: 'VIDEOCLIP_ADD',
      payload: {
        src: videoAsset,
        fileName,
        id: ids.generate(),
        integrationData: {
          id: INTEGRATION_ID,
        },
        original: undefined,
        enableBlur: true,
        framingMethod,
        fitType: isAutoFrame ? 'fill' : 'fit',
        trimStartMillis: startMillis,
        trimEndMillis: endMillis,
        metadata: {
          targetLayerIndex: 0,
        },
      },
    });

    setIsAddingPlaceholderVideo(false);
  }, [
    createVideoAsset,
    fileName,
    framingMethod,
    isAutoFrame,
    src,
    templateDispatch,
    trimEndMillis,
    trimStartMillis,
  ]);

  const handleReplaceWithPlaceholderVideo = useCallback(async (): Promise<
    void
  > => {
    setIsAddingPlaceholderVideo(true);

    const topMostVideoClipId = first(getStateContent(state)?.videoClips?.order);
    const { videoAsset, startMillis, endMillis } = await createVideoAsset(
      src,
      trimStartMillis,
      trimEndMillis,
    );

    templateDispatch({
      type: 'VIDEOCLIP_REPLACE',
      payload: {
        src: videoAsset,
        fileName,
        id: topMostVideoClipId,
        integrationData: {
          id: INTEGRATION_ID,
        },
        original: undefined,
        framingMethod,
        fitType: isAutoFrame ? 'fill' : 'fit',
        trimStartMillis: startMillis,
        trimEndMillis: endMillis,
        previewType: 'videoClip',
      },
    });

    if (topMostVideoClipId && framingMethod) {
      templateDispatch({
        type: 'VIDEO_FRAMING_METHOD_CHANGE',
        payload: { id: topMostVideoClipId, framingMethod },
      });
    }

    setIsAddingPlaceholderVideo(false);
  }, [
    createVideoAsset,
    fileName,
    framingMethod,
    isAutoFrame,
    src,
    state,
    templateDispatch,
    trimEndMillis,
    trimStartMillis,
  ]);

  useEffect(() => {
    if (!placeholderVideo || !EditorState.getContent(state)?.canvas) {
      return;
    }

    if (
      prevTemplateRef.current !== template &&
      !isVideoReplacedRef.current &&
      isActive
    ) {
      // If the template is a blank canvas (undefined) or if the template has
      // no video clips, add the placeholder video.
      if (template === undefined || !templateClipsCount) {
        handleAddPlaceholderVideo();
        onIsAutoFrameChange(isAutoFrame);
      }

      // If the template has video clips and the state has video clips, replace
      // the top most video clip in the template with the placeholder video.
      if (templateClipsCount && stateClipsCount) {
        handleReplaceWithPlaceholderVideo();
        onIsAutoFrameChange(isAutoFrame);
      }

      prevTemplateRef.current = template;
      isVideoReplacedRef.current = true;
    }
  }, [
    handleAddPlaceholderVideo,
    handleReplaceWithPlaceholderVideo,
    isActive,
    isAutoFrame,
    onIsAutoFrameChange,
    placeholderVideo,
    state,
    stateClipsCount,
    template,
    templateClipsCount,
  ]);

  useEffect(() => {
    isVideoReplacedRef.current = false;
    prevTemplateRef.current = null;
    setIsAddingPlaceholderVideo(true);
  }, [template]);

  return {
    isAddingPlaceholderVideo,
    priority,
    creationType: 'dynamic',
    type: 'video',
    id: INTEGRATION_ID,
    postProcessor: identity,
    compatibleIntegrationIds: [INTEGRATION_ID],
    childViewProps: {
      title: childViewProps?.title ?? 'video podcast',
      body: childViewProps?.body ?? (
        <VideoPodcastSettings onIsAutoFrameChange={onIsAutoFrameChange} />
      ),
    },
    onAddMedia: noop,
    onReplaceMedia: noop,
    onDeleteMedia: noop,
  };
}
